import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, Heart, Store } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import AssuntosPicker from "@/components/AssuntosPicker";
import { ASSUNTOS_POR_ETAPA, OUTROS, expandAssuntos } from "@/data/assuntos";

const ETAPAS = ["Planejamento", "Gestação", "Parto", "Pós-parto"];
const inputClass =
  "w-full bg-white border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm text-maternnia-charcoal placeholder:text-maternnia-taupe/70 focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto transition";

const EntrarPage = () => {
  const { user, login, register, loginGoogle } = useAuth();
  const navigate = useNavigate();
  const [modo, setModo] = useState("entrar"); // entrar | criar
  const [role, setRole] = useState("familia");
  const [form, setForm] = useState({ nome: "", email: "", senha: "", etapa: "", cidade: "", assuntos: [], outrosTexto: "" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEtapa = (etapa) => setForm((f) => {
    const validos = [...(ASSUNTOS_POR_ETAPA[etapa] || []), OUTROS];
    return { ...f, etapa, assuntos: f.assuntos.filter((a) => validos.includes(a)) };
  });
  const toggleAssunto = (a) => setForm((f) => ({ ...f, assuntos: f.assuntos.includes(a) ? f.assuntos.filter((x) => x !== a) : [...f.assuntos, a] }));

  useEffect(() => {
    if (user) navigate(user.role === "fornecedor" ? "/painel-fornecedor" : "/minha-conta", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (modo === "entrar") {
        const u = await login(form.email, form.senha);
        navigate(u.role === "fornecedor" ? "/painel-fornecedor" : "/minha-conta", { replace: true });
      } else {
        const u = await register({ nome: form.nome, email: form.email, senha: form.senha, role, etapa: form.etapa, cidade: form.cidade, assuntos: expandAssuntos(form.assuntos, form.outrosTexto) });
        toast.success("Conta criada! Bem-vinda à Maternnia.");
        navigate(u.role === "fornecedor" ? "/painel-fornecedor" : "/minha-conta", { replace: true });
      }
    } catch (err) {
      toast.error("Não foi possível continuar", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="entrar-page" className="bg-maternnia-porcelana/40 min-h-screen flex items-center py-16">
      <Seo title={modo === "entrar" ? "Entrar" : "Criar conta"} path="/entrar" noindex />
      <div className="max-w-md w-full mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white rounded-[2rem] p-8 md:p-10 shadow-[0_24px_64px_rgba(43,37,38,0.10)]"
        >
          <h1 className="font-serif text-3xl text-maternnia-charcoal font-medium text-center">
            {modo === "entrar" ? "Entrar na sua conta" : "Criar sua conta"}
          </h1>
          <p className="mt-2 text-sm text-maternnia-taupe font-light text-center">Sua jornada, com mais clareza, confiança e apoio.</p>

          {/* Role selector */}
          <div className="mt-6 grid grid-cols-2 gap-2 bg-maternnia-porcelana/60 p-1.5 rounded-2xl">
            <button type="button" data-testid="role-familia" onClick={() => setRole("familia")}
              className={`flex items-center justify-center gap-1.5 rounded-xl py-2.5 px-2 text-[13px] leading-tight text-center font-medium transition-all ${role === "familia" ? "bg-white text-maternnia-charcoal shadow-sm" : "text-maternnia-taupe"}`}>
              <Heart size={15} className="shrink-0" /> Sou ou pretendo ser mãe
            </button>
            <button type="button" data-testid="role-fornecedor" onClick={() => setRole("fornecedor")}
              className={`flex items-center justify-center gap-1.5 rounded-xl py-2.5 px-2 text-[13px] leading-tight text-center font-medium transition-all ${role === "fornecedor" ? "bg-white text-maternnia-charcoal shadow-sm" : "text-maternnia-taupe"}`}>
              <Store size={15} className="shrink-0" /> Sou fornecedor
            </button>
          </div>

          <form onSubmit={submit} data-testid="entrar-form" className="mt-6 space-y-4">
            {modo === "criar" && (
              <input data-testid="in-nome" type="text" required placeholder="Seu nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} className={inputClass} />
            )}
            <input data-testid="in-email" type="email" required placeholder="E-mail" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputClass} />
            <input data-testid="in-senha" type="password" required placeholder="Senha (mín. 8 caracteres)" value={form.senha} onChange={(e) => set("senha", e.target.value)} className={inputClass} />
            {modo === "criar" && role === "familia" && (
              <select data-testid="in-etapa" value={form.etapa} onChange={(e) => setEtapa(e.target.value)} className={`${inputClass} ${form.etapa === "" ? "text-maternnia-taupe/70" : ""}`}>
                <option value="">Sua etapa atual (opcional)</option>
                {ETAPAS.map((x) => <option key={x}>{x}</option>)}
              </select>
            )}
            {modo === "criar" && role === "familia" && (
              <AssuntosPicker etapaNome={form.etapa} assuntos={form.assuntos} onToggle={toggleAssunto} outrosTexto={form.outrosTexto} onOutrosChange={(v) => set("outrosTexto", v)} inputClass={inputClass} label="Assuntos de interesse (opcional)" />
            )}
            {modo === "criar" && role === "fornecedor" && (
              <p className="text-xs text-maternnia-taupe font-light bg-maternnia-porcelana/60 rounded-xl px-4 py-3">
                O acesso de fornecedor é liberado após a aprovação da curadoria. Ainda não é parceiro?{" "}
                <Link to="/fornecedores" className="text-maternnia-eucalipto underline underline-offset-2">Cadastre-se aqui</Link>.
              </p>
            )}
            <button type="submit" data-testid="entrar-submit" disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-maternnia-eucalipto text-white hover:bg-maternnia-floresta rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 disabled:opacity-70">
              {busy ? <Loader2 size={18} className="animate-spin" /> : (<>{modo === "entrar" ? "Entrar" : "Criar conta"} <ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-maternnia-taupe/70">
            <span className="flex-1 h-px bg-maternnia-nevoa" /> ou <span className="flex-1 h-px bg-maternnia-nevoa" />
          </div>

          <button type="button" data-testid="google-btn" onClick={() => loginGoogle(role)}
            className="w-full inline-flex items-center justify-center gap-3 border border-maternnia-nevoa rounded-full px-6 py-3 text-sm font-medium text-maternnia-charcoal hover:bg-maternnia-porcelana/50 transition">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" /> Continuar com o Google
          </button>

          <p className="mt-6 text-center text-sm text-maternnia-taupe font-light">
            {modo === "entrar" ? "Ainda não tem conta?" : "Já tem uma conta?"}{" "}
            <button type="button" data-testid="toggle-modo" onClick={() => setModo(modo === "entrar" ? "criar" : "entrar")} className="text-maternnia-eucalipto font-medium underline underline-offset-2">
              {modo === "entrar" ? "Criar conta" : "Entrar"}
            </button>
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default EntrarPage;
